import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { 
    FaArrowLeft, 
    FaFileDownload, 
    FaFileUpload, 
    FaCloudUploadAlt,
    FaFileExcel,
    FaCheckCircle
} from 'react-icons/fa';
import { toast } from 'react-toastify';

const EngineerAddBulkWork = () => {
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            toast.success(`${selectedFile.name} निवडली गेली आहे.`);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            setFile(droppedFile);
            toast.success(`${droppedFile.name} निवडली गेली आहे.`);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!file) {
            toast.error("कृपया फाईल निवडा (Please select a file)");
            return;
        }
        // Simulation of bulk upload
        toast.info("फाईल अपलोड प्रक्रिया सुरू होत आहे... (Starting upload...)");
        setTimeout(() => {
            toast.success("सर्व कामे यशस्वीरित्या जोडली गेली आहेत! (Bulk works added successfully!)");
            navigate('/engineer/work-names-list');
        }, 2000);
    };

    return (
        <EngineerLayout>
            <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-sans">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 max-w-[1200px] mx-auto gap-4">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate('/engineer/work-info')}
                            className="p-3 bg-white rounded-2xl shadow-sm border border-gray-100 text-gray-400 hover:text-[#1a1a4d] transition-all active:scale-95"
                        >
                            <FaArrowLeft />
                        </button>
                        <div>
                            <h1 className="text-2xl font-black text-gray-800 tracking-tight">मोठ्या प्रमाणात काम जोडा</h1>
                            <p className="text-[#e53935] font-bold text-[10px] uppercase tracking-widest mt-1">Add Bulk Work (Excel/CSV)</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-[12px] font-bold text-gray-400 bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-50">
                        <span>सांगली जिल्हा परिषद</span>
                        <span className="text-gray-200">/</span>
                        <span className="text-blue-500 uppercase tracking-tighter">Bulk Upload</span>
                    </div>
                </div>

                {/* Main Content Card */}
                <div className="max-w-[1000px] mx-auto">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-blue-100/30 border border-gray-100 overflow-hidden transform transition-all">
                        {/* Top Accent Bar */}
                        <div className="h-2 bg-gradient-to-r from-red-500 via-orange-500 to-red-600"></div>

                        <div className="p-8 md:p-15">
                            <form onSubmit={handleSubmit} className="space-y-10">
                                {/* Upload Zone */}
                                <div className="space-y-4">
                                    <label className="block text-sm font-black text-gray-700 ml-2">
                                        एक्सेल किंवा सीएसव्ही फाईल निवडा <span className="text-red-500">*</span>
                                    </label>
                                    
                                    <div 
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        className={`relative border-3 border-dashed rounded-[2rem] p-12 transition-all duration-300 flex flex-col items-center justify-center gap-6 group cursor-pointer ${
                                            isDragging 
                                            ? 'border-blue-500 bg-blue-50/50 scale-[0.99] shadow-inner' 
                                            : 'border-gray-200 bg-gray-50/30 hover:border-red-400 hover:bg-red-50/20'
                                        }`}
                                    >
                                        <input 
                                            type="file" 
                                            onChange={handleFileChange}
                                            className="absolute inset-0 opacity-0 cursor-pointer z-10"
                                            accept=".csv, .xlsx, .xls"
                                        />
                                        
                                        <div className={`p-6 rounded-full transition-transform duration-500 group-hover:scale-110 ${
                                            file ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-500'
                                        }`}>
                                            {file ? <FaCheckCircle size={40} /> : <FaCloudUploadAlt size={40} />}
                                        </div>

                                        <div className="text-center">
                                            {file ? (
                                                <div className="space-y-2">
                                                    <p className="text-lg font-black text-gray-800">{file.name}</p>
                                                    <p className="text-xs font-bold text-gray-400 uppercase">फाईल निवडली गेली आहे</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    <p className="text-lg font-black text-gray-700">फाईल येथे ओढा किंवा क्लिक करा</p>
                                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Supports: .xlsx, .xls, .csv</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Information & Download Section */}
                                <div className="bg-blue-50/50 rounded-3xl p-6 border border-blue-100/50 flex flex-col md:flex-row items-center justify-between gap-6">
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 bg-white rounded-2xl shadow-sm">
                                            <FaFileExcel className="text-green-600 text-xl" />
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-black text-gray-800 leading-tight">नमुना फाईल (Sample File)</h4>
                                            <p className="text-[11px] font-bold text-gray-500 mt-0.5">डेटा भरण्यापूर्वी नमुना फाईल पहा</p>
                                        </div>
                                    </div>
                                    
                                    <button 
                                        type="button"
                                        onClick={() => toast.info("नमुना फाईल डाउनलोड सुरू होत आहे...")}
                                        className="flex items-center gap-3 px-6 py-3 bg-white border border-gray-100 rounded-2xl text-blue-600 text-xs font-black shadow-sm hover:shadow-md transition-all active:scale-95 group"
                                    >
                                        <FaFileDownload className="group-hover:translate-y-0.5 transition-transform" /> 
                                        Sample Download
                                    </button>
                                </div>

                                {/* Action Buttons */}
                                <div className="pt-4 flex items-center gap-4">
                                    <button 
                                        type="submit"
                                        className="flex-grow md:flex-none px-12 py-4 bg-[#e53935] hover:bg-red-700 text-white rounded-2xl font-black text-sm shadow-xl shadow-red-200 transition-all transform active:scale-95 flex items-center justify-center gap-3"
                                    >
                                        <FaFileUpload /> अपलोड करा (Upload Now)
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => navigate('/engineer/work-info')}
                                        className="hidden md:block px-8 py-4 bg-gray-100 text-gray-500 hover:bg-gray-200 rounded-2xl font-black text-sm transition-all active:scale-95"
                                    >
                                        रद्द करा
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <div className="mt-12 text-center pb-8 opacity-30 select-none">
                    <p className="text-[10px] font-black uppercase tracking-[0.5em] text-gray-400">
                        Sangli Zilla Parishad - E-Work Allocation
                    </p>
                </div>
            </div>
        </EngineerLayout>
    );
};

export default EngineerAddBulkWork;
